import { StyleSheet, css } from 'aphrodite'
import type React from 'react'

interface ConfirmDeclineModalProps {
  /** The entity type being declined (e.g., "Project Terms", "Tag Terms", "Dispatch") */
  entityName: string
  /** Whether the form is currently submitting */
  isSubmitting?: boolean
  /** Called when user confirms decline without comment */
  onConfirm: () => void
  /** Called when user wants to decline with a comment */
  onDeclineWithComment: () => void
  /** Called when user cancels the decline action */
  onCancel: () => void
}

const ConfirmDeclineModal: React.FC<ConfirmDeclineModalProps> = ({
  entityName,
  isSubmitting = false,
  onConfirm,
  onDeclineWithComment,
  onCancel,
}) => {
  return (
    <div className={css(styles.confirmationOverlay)}>
      <div className={css(styles.confirmationModal)}>
        <div className={css(styles.confirmationHeader)}>
          <h3 className={css(styles.confirmationTitle)}>Confirm Decline</h3>
        </div>
        <div className={css(styles.confirmationContent)}>
          <p className={css(styles.confirmationText)}>
            By selecting and then clicking on the [Decline] button I indicate I
            expressly decline the provisions of the {entityName}.
          </p>
        </div>
        <div className={css(styles.declineConfirmationFooter)}>
          <div className={css(styles.declineButtonGroup)}>
            <button
              onClick={onDeclineWithComment}
              className={css(
                styles.declineWithCommentBtn,
                styles.declineButtonEqual,
              )}
              type="button"
            >
              <i className="fa-solid fa-comment" />
              Decline with Comment
            </button>
            <button
              onClick={onConfirm}
              className={css(
                styles.confirmationDeclineBtn,
                styles.declineButtonEqual,
              )}
              disabled={isSubmitting}
              type="button"
            >
              {isSubmitting ? 'Processing...' : `Yes, I decline ${entityName}`}
            </button>
          </div>
          <button
            onClick={onCancel}
            className={css(styles.declineCancelBtn)}
            type="button"
          >
            No, I made an error
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  confirmationOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  confirmationModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '550px',
    margin: '20px',
  },
  confirmationHeader: {
    padding: '24px 24px 0 24px',
  },
  confirmationTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  confirmationContent: {
    padding: '16px 24px',
  },
  confirmationText: {
    margin: 0,
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
  },
  declineConfirmationFooter: {
    padding: '16px 24px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  declineButtonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  declineButtonEqual: {
    flex: '1',
  },
  declineCancelBtn: {
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'center',
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
  },
  confirmationDeclineBtn: {
    backgroundColor: '#DC2626',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#B91C1C',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  declineWithCommentBtn: {
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
  },
})

export default ConfirmDeclineModal
